import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'

export const LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY =
  'lvdProductComplianceFiles' as const

export type ComplianceFileStatus = 'ACTIVE' | 'IDLE'

export type ComplianceActor = 'OEM' | 'SUPPLIER'

export type ConfirmedLvdStandard = {
  designation: string
  title?: string
}

export type LvdStandardRecommendation = {
  designation: string
  title: string | null
  reason: string | null
}

export type LvdProductComplianceFileView = {
  id: string
  status: ComplianceFileStatus
  completedAt: string | null
  completedByActor: ComplianceActor | null
  confirmedStandardsJson: ConfirmedLvdStandard[] | null
  latestOutputDocumentId: string | null
  latestDeclarationNumber: string | null
  createdAt: string
  updatedAt: string
}

export type LvdProductComplianceFileOutputDocumentStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'

export type LvdProductComplianceFileOutputDocument = {
  id: string
  lvdProductComplianceFileId: string
  documentType: string
  declarationNumber: string | null
  fileName: string
  fileSizeKb: number | null
  contentType: string | null
  createdByActor: ComplianceActor | null
  createdByUserId: string | null
  status: LvdProductComplianceFileOutputDocumentStatus
  errorMessage: string | null
  statusUpdatedAt: string | null
  workflowRunId: string | null
  createdAt: string
  updatedAt: string
}

export type LvdProductComplianceFilesState = {
  product: LvdProductComplianceFileView | null
  outputDocuments: LvdProductComplianceFileOutputDocument[]
}

export function useGetLvdProductComplianceFiles(
  productId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<LvdProductComplianceFilesState, Error>({
    queryKey: [LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
    queryFn: async () => {
      const response = await apiClient.get<LvdProductComplianceFilesState>(
        `/v2/products/${productId}/lvd/compliance-file`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) && productId != null && productId.length > 0,
  })
}

export type CompleteLvdProductComplianceFileResult = {
  product: LvdProductComplianceFileView
  warnings: string[]
}

export type UncompleteLvdProductComplianceFileResult = {
  product: LvdProductComplianceFileView
}

export function useCompleteLvdProductComplianceFile(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<CompleteLvdProductComplianceFileResult, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<CompleteLvdProductComplianceFileResult>(
        `/v2/products/${productId}/lvd/compliance-file/complete`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function useUncompleteLvdProductComplianceFile(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<UncompleteLvdProductComplianceFileResult, Error, void>({
    mutationFn: async () => {
      const response =
        await apiClient.post<UncompleteLvdProductComplianceFileResult>(
          `/v2/products/${productId}/lvd/compliance-file/uncomplete`
        )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export type ReplaceLvdConfirmedStandardsResult = {
  product: LvdProductComplianceFileView
  standards: ConfirmedLvdStandard[]
}

export type LvdStandardsRecommendationsView = {
  standards: LvdStandardRecommendation[]
}

export function useReplaceLvdConfirmedStandards(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    ReplaceLvdConfirmedStandardsResult,
    Error,
    { standards: ConfirmedLvdStandard[] }
  >({
    mutationFn: async (payload) => {
      const response = await apiClient.put<ReplaceLvdConfirmedStandardsResult>(
        `/v2/products/${productId}/lvd/compliance-file/confirmed-standards`,
        payload
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData<LvdProductComplianceFilesState>(
        [LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
        (current) => ({
          product: data.product,
          outputDocuments: current?.outputDocuments ?? [],
        })
      )
      void queryClient.invalidateQueries({
        queryKey: [LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function useGetLvdStandardsRecommendations(productId: string) {
  return useMutation<LvdStandardsRecommendationsView, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<LvdStandardsRecommendationsView>(
        `/v2/products/${productId}/lvd/compliance-file/standards-recommendations`
      )
      return response.data
    },
  })
}

const LVD_PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_GENERATE_TIMEOUT_MS =
  180_000

type LvdProductComplianceFileDeclarationOfConformityStreamEvent =
  | { type: 'workflow.started' }
  | { type: 'step.started'; step: string }
  | { type: 'step.completed'; step: string }
  | { type: 'progress'; message: string; temporary?: boolean }
  | {
      type: 'workflow.completed'
      result?: {
        outputDocumentId: string
        declarationNumber: string
        fileName: string
      }
    }
  | { type: 'error'; error: string }
  | { type: 'workflow.failed'; error?: string }

function getApiStreamUrl(path: string): string {
  const baseURL = apiClient.defaults.baseURL ?? ''
  return `${baseURL}${path}`
}

function getApiStreamHeaders(): Record<string, string> {
  const common = (apiClient.defaults.headers.common ?? {}) as Record<
    string,
    string
  >
  const { 'Content-Type': _contentType, ...rest } = common
  return rest
}

async function downloadLvdProductComplianceFileOutputDocumentFromUrl(
  downloadUrl: string,
  fileName: string
) {
  const response = await fetch(downloadUrl)
  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`)
  }
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  link.click()
  URL.revokeObjectURL(objectUrl)
}

export function useDownloadLvdProductComplianceFileOutputDocument(
  productId: string
) {
  return useMutation<
    void,
    Error,
    { outputDocumentId: string; fileName?: string }
  >({
    mutationFn: async ({ outputDocumentId, fileName }) => {
      const response = await apiClient.get<{
        downloadUrl: string
        fileName: string
        declarationNumber: string | null
      }>(
        `/v2/products/${productId}/lvd/compliance-file/output-documents/${outputDocumentId}/download-url`
      )
      await downloadLvdProductComplianceFileOutputDocumentFromUrl(
        response.data.downloadUrl,
        fileName ?? response.data.fileName
      )
    },
  })
}

export function useStartLvdProductComplianceFileDeclarationOfConformityGeneration(
  productId: string
) {
  const queryClient = useQueryClient()

  const invalidateFiles = () => {
    void queryClient.invalidateQueries({
      queryKey: [LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
    })
  }

  return useMutation<
    void,
    Error,
    {
      manufacturerName: string
      manufacturerAddress: string
      onEvent?: (
        event: LvdProductComplianceFileDeclarationOfConformityStreamEvent
      ) => void
      onComplete?: (result: {
        outputDocumentId: string
        declarationNumber: string
        fileName: string
      }) => void
    }
  >({
    mutationFn: async ({
      manufacturerName,
      manufacturerAddress,
      onEvent,
      onComplete,
    }) => {
      const url = getApiStreamUrl(
        `/v2/products/${productId}/lvd/compliance-file/declaration-of-conformity/generate`
      )
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getApiStreamHeaders(),
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          manufacturerName,
          manufacturerAddress,
        }),
        signal: AbortSignal.timeout(
          LVD_PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_GENERATE_TIMEOUT_MS
        ),
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start Declaration of Conformity generation: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }

      invalidateFiles()

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let hasInvalidatedOnProgress = false

      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(
              data
            ) as LvdProductComplianceFileDeclarationOfConformityStreamEvent & {
              type: string
            }
            if (
              !hasInvalidatedOnProgress &&
              (event.type === 'workflow.started' ||
                event.type === 'step.started' ||
                event.type === 'progress')
            ) {
              hasInvalidatedOnProgress = true
              invalidateFiles()
            }
            if (
              event.type === 'progress' ||
              event.type === 'step.started' ||
              event.type === 'step.completed' ||
              event.type === 'error'
            ) {
              onEvent?.(event)
            }
            if (event.type === 'workflow.completed') {
              const completedEvent = event as Extract<
                LvdProductComplianceFileDeclarationOfConformityStreamEvent,
                { type: 'workflow.completed' }
              >
              if (completedEvent.result) {
                await onComplete?.(completedEvent.result)
              }
              return
            }
            if (event.type === 'error') {
              throw new Error(event.error ?? 'Generation failed')
            }
            if (event.type === 'workflow.failed') {
              throw new Error(
                (event as { error?: string }).error ?? 'Workflow failed'
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      invalidateFiles()
    },
    onError: () => {
      invalidateFiles()
    },
  })
}
